import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  EventNote as EventNoteIcon,
  PersonOutline as PersonOutlineIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import api from '../config/api';
import { getTripPrimaryLocation, getTripAllLocations } from '../utils/tripUtils';
import PartnerWorkspaceCard, {
  partnerCardTitleSx,
} from '../components/PartnerWorkspaceCard';
import PartnerTripDetailsEditor from '../components/PartnerTripDetailsEditor';
import {
  PartnerEmptyState,
  PartnerFilterBar,
  PartnerLoading,
  PartnerNote,
  PartnerPageHeader,
  PartnerPageShell,
  partnerOxbloodBtnSx,
  partnerOutlineBtnSx,
} from '../components/PartnerListChrome';
import PartnerTripPipelineBoard from '../components/PartnerTripPipelineBoard';
import PartnerDataExportButton from '../components/PartnerDataExportButton';
import PartnerTeamMemberFilter from '../components/PartnerTeamMemberFilter';
import EmailForwardingCard from '../components/EmailForwardingCard';
import { applyPipelineStage, pipelineBoardId } from '../utils/pipelineBoardDragUtils';
import { persistPartnerTripsView, resolvePartnerTripsView } from '../utils/partnerWorkspace';
import { buildAssigneeFilterParams } from '../utils/partnerAssigneeFilter';
import { getPartnerSessionFromToken } from '../utils/partnerPush';
import { getPartnerTripPath } from '../utils/partnerTripPaths';
import { DOCUMENT } from '../constants/documentTheme';
import {
  canArchivePartnerTrip,
  canDeletePartnerTrip,
  getTripBucket,
  getTripClientName,
  getTravelerDisplayName,
  getTravelerInitials,
  getDestinationPhotoUrl,
  matchesQuery,
} from '../utils/partnerListUtils';

const OPS_MODES = new Set(['traveling', 'departing', 'flights']);

const OPS_LIST_TEST_IDS = {
  traveling: 'partner-ops-traveling-list',
  departing: 'partner-ops-departing-list',
  flights: 'partner-ops-flights-list',
};

const opsThSx = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.19em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  pt: 1.9,
  pb: 1.35,
  px: 2,
  borderBottom: `1px solid ${DOCUMENT.rule}`,
};

const opsTdSx = {
  py: 1.75,
  px: 2,
  fontSize: 13.5,
  verticalAlign: 'top',
  borderBottom: `1px solid ${DOCUMENT.rule}`,
};

const formatOpsDateRange = (start, end) => {
  if (!start) return '—';
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  if (!endDate) return format(startDate, 'MMM d, yyyy');
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  return `${format(startDate, sameYear ? 'MMM d' : 'MMM d, yyyy')} – ${format(endDate, 'MMM d, yyyy')}`;
};

const formatEventWhen = (value) => {
  if (!value) return '';
  const parsed = parseISO(String(value).replace(' ', 'T'));
  return format(parsed, 'h:mm a');
};

const STATUS_DOT = {
  on_track: '#2E7D32',
  needs_attention: '#F9A825',
  urgent: '#C62828',
  awaiting_client: '#1565C0',
  awaiting_supplier: '#6D4C41',
};

const DEFAULT_PIPELINE_STAGES = [
  'inquiry',
  'planning',
  'proposal_sent',
  'approved',
  'booked',
  'traveling',
  'completed',
];

const getTripImageUrl = (location, _tripType, tripId) => (
  getDestinationPhotoUrl(location === 'No destination set' ? '' : location, tripId)
);

const PartnerTrips = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const partnerSession = useMemo(() => getPartnerSessionFromToken(), []);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('departure');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [successNotice, setSuccessNotice] = useState('');
  const [viewMode, setViewModeState] = useState(() => resolvePartnerTripsView(searchParams.toString()));
  const [pipelineTrips, setPipelineTrips] = useState([]);
  const [pipelineStages, setPipelineStages] = useState(DEFAULT_PIPELINE_STAGES);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [stageUpdatingId, setStageUpdatingId] = useState(null);
  const [teamMemberFilter, setTeamMemberFilter] = useState(
    partnerSession.isPartnerUser ? 'mine' : 'all'
  );
  const opsMode = searchParams.get('ops');
  const isOpsMode = OPS_MODES.has(opsMode);
  const [opsLoading, setOpsLoading] = useState(false);
  const [travelingRows, setTravelingRows] = useState([]);
  const [travelingStatusFilter, setTravelingStatusFilter] = useState('all');
  const [departingRows, setDepartingRows] = useState([]);
  const [flightAlerts, setFlightAlerts] = useState([]);

  const getPartnerAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const assigneeQueryString = useMemo(() => {
    const params = buildAssigneeFilterParams(teamMemberFilter);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [teamMemberFilter]);

  const setViewMode = (mode) => {
    const nextMode = mode === 'board' ? 'board' : 'list';
    persistPartnerTripsView(nextMode);
    setViewModeState(nextMode);
    const next = new URLSearchParams(searchParams);
    if (nextMode === 'board') next.set('view', 'board');
    else next.delete('view');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const next = resolvePartnerTripsView(searchParams.toString());
    setViewModeState(next);
    persistPartnerTripsView(next);
  }, [searchParams]);

  useEffect(() => {
    if (isOpsMode) return;
    fetchTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpsMode]);

  useEffect(() => {
    if (isOpsMode || viewMode !== 'board') return;
    fetchPipelineTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, teamMemberFilter, isOpsMode]);

  useEffect(() => {
    if (!isOpsMode) return;
    fetchOpsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opsMode, isOpsMode]);

  const fetchOpsData = async () => {
    try {
      setOpsLoading(true);
      setError('');
      const token = localStorage.getItem('partnerToken');
      if (!token) {
        navigate('/partner/login');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      if (opsMode === 'traveling') {
        const response = await api.get('/partners/operations/traveling', { headers });
        setTravelingRows(response.data.trips || []);
      } else if (opsMode === 'departing') {
        const response = await api.get('/partners/operations/departing', { headers });
        setDepartingRows(response.data.trips || []);
      } else if (opsMode === 'flights') {
        const response = await api.get('/partners/flight-alerts', { headers });
        setFlightAlerts(response.data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching partner ops list:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('partnerToken');
        localStorage.removeItem('partner');
        navigate('/partner/login');
      } else {
        setError(t('partnerLists.loadTripsFailed'));
      }
    } finally {
      setOpsLoading(false);
    }
  };

  const fetchPipelineTrips = async () => {
    try {
      setPipelineLoading(true);
      const token = localStorage.getItem('partnerToken');
      if (!token) {
        navigate('/partner/login');
        return;
      }
      const response = await api.get(`/partners/pipeline/trips${assigneeQueryString}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPipelineTrips(response.data.trips || []);
      if (Array.isArray(response.data.stages) && response.data.stages.length) {
        setPipelineStages(response.data.stages);
      }
    } catch (err) {
      console.error('Error fetching pipeline trips:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('partnerToken');
        localStorage.removeItem('partner');
        navigate('/partner/login');
      } else {
        setError(t('partnerLists.loadTripsFailed'));
      }
    } finally {
      setPipelineLoading(false);
    }
  };

  const handlePipelineStageChange = async (boardId, stage) => {
    const current = pipelineTrips.find((trip) => pipelineBoardId(trip) === boardId);
    if (!current || current.pipeline_stage === stage) return;
    if (current.cardType === 'inquiry') {
      if (stage === 'inquiry') return;
      try {
        setStageUpdatingId(boardId);
        await api.post(
          `/package-booking/partner/inquiries/${current.inquiryId}/convert-to-trip`,
          { pipelineStage: stage },
          { headers: getPartnerAuthHeaders() }
        );
        await fetchPipelineTrips();
      } catch (err) {
        setError(err.response?.data?.error || t('partnerPipeline.stageUpdateFailed'));
      } finally {
        setStageUpdatingId(null);
      }
      return;
    }
    setPipelineTrips((prev) => applyPipelineStage(prev, boardId, stage));
    try {
      setStageUpdatingId(boardId);
      await api.patch(`/partners/trips/${current.tripId || current.id}/pipeline-stage`, { stage }, {
        headers: getPartnerAuthHeaders(),
      });
    } catch (err) {
      setError(err.response?.data?.error || t('partnerPipeline.stageUpdateFailed'));
      await fetchPipelineTrips();
    } finally {
      setStageUpdatingId(null);
    }
  };

  const fetchTrips = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('partnerToken');

      if (!token) {
        navigate('/partner/login');
        return;
      }

      const response = await api.get('/partners/trips', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setTrips(response.data.trips || []);
    } catch (err) {
      console.error('Error fetching trips:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('partnerToken');
        localStorage.removeItem('partner');
        navigate('/partner/login');
      } else {
        setError(t('partnerLists.loadTripsFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrip = async () => {
    if (!selectedTrip) return;
    try {
      setActionLoading(true);
      await api.delete(`/trips/${selectedTrip.id}`, {
        headers: getPartnerAuthHeaders(),
      });
      setDeleteDialogOpen(false);
      setSelectedTrip(null);
      await fetchTrips();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.deleteTripFailed'));
      setDeleteDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchiveTrip = async (archived = true) => {
    if (!selectedTrip) return;
    try {
      setActionLoading(true);
      await api.patch(`/partners/trips/${selectedTrip.id}/archive`, { archived }, {
        headers: getPartnerAuthHeaders(),
      });
      setArchiveDialogOpen(false);
      setSelectedTrip(null);
      await fetchTrips();
    } catch (err) {
      setError(err.response?.data?.error || (
        archived ? t('partnerLists.archiveTripFailed') : t('partnerLists.restoreTripFailed')
      ));
      setArchiveDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkArchiveCompletedTrips = async () => {
    try {
      setActionLoading(true);
      setError('');
      const response = await api.patch('/partners/trips/archive-past', {}, {
        headers: getPartnerAuthHeaders(),
      });
      const archivedCount = Number(response?.data?.archivedCount || 0);
      setBulkArchiveDialogOpen(false);
      setSuccessNotice(
        archivedCount > 0
          ? t('partnerLists.archiveAllPastSuccess', { count: archivedCount })
          : t('partnerLists.archiveAllPastNone')
      );
      await fetchTrips();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.archiveAllPastFailed'));
      setBulkArchiveDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const completedCount = useMemo(
    () => trips.filter((trip) => getTripBucket(trip) === 'completed').length,
    [trips]
  );

  const searchedTrips = useMemo(() => trips.filter((trip) => {
    if (getTripBucket(trip) === 'archived') return false;
    return matchesQuery([
      trip.title,
      getTripClientName(trip),
      trip.owner_email,
      getTripPrimaryLocation(trip),
      getTripAllLocations(trip),
      trip.trip_code,
    ], search);
  }), [trips, search]);

  const stageCounts = useMemo(() => {
    const next = { all: searchedTrips.length };
    DEFAULT_PIPELINE_STAGES.forEach((stage) => {
      next[stage] = searchedTrips.filter((trip) => (trip.pipeline_stage || 'planning') === stage).length;
    });
    return next;
  }, [searchedTrips]);

  const tripMatchesTeam = useCallback((trip) => {
    if (!teamMemberFilter || teamMemberFilter === 'all') return true;
    const assigneeId = trip.assigned_partner_user_id;
    if (teamMemberFilter === 'anyone') return assigneeId == null || assigneeId === '';
    if (teamMemberFilter === 'mine') {
      return String(assigneeId || '') === String(partnerSession.partnerUserId || '');
    }
    return String(assigneeId || '') === String(teamMemberFilter);
  }, [teamMemberFilter, partnerSession.partnerUserId]);

  const handleListSearch = (value) => {
    setSearch(value);
    if (String(value || '').trim()) setStageFilter('all');
  };

  const advanceListTripStage = async (trip, stage) => {
    if (!trip?.id || (trip.pipeline_stage || 'planning') === stage) return;
    setTrips((prev) => prev.map((row) => (
      row.id === trip.id ? { ...row, pipeline_stage: stage } : row
    )));
    try {
      await api.patch(`/partners/trips/${trip.id}/pipeline-stage`, { stage }, {
        headers: getPartnerAuthHeaders(),
      });
    } catch (err) {
      setError(err.response?.data?.error || t('partnerPipeline.stageUpdateFailed'));
      await fetchTrips();
    }
  };

  const visibleTrips = useMemo(() => {
    const filtered = searchedTrips.filter((trip) => {
      if (stageFilter !== 'all' && (trip.pipeline_stage || 'planning') !== stageFilter) {
        return false;
      }
      return tripMatchesTeam(trip);
    });

    return [...filtered].sort((a, b) => {
      if (sort === 'name') {
        return String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' });
      }
      if (sort === 'client') {
        return getTripClientName(a).localeCompare(getTripClientName(b), undefined, { sensitivity: 'base' });
      }
      if (sort === 'destination') {
        return String(getTripPrimaryLocation(a) || a.destination || '')
          .localeCompare(String(getTripPrimaryLocation(b) || b.destination || ''), undefined, { sensitivity: 'base' });
      }
      if (sort === 'departure') {
        return new Date(b.start_date || 0) - new Date(a.start_date || 0);
      }
      if (sort === 'return') {
        return new Date(b.end_date || 0) - new Date(a.end_date || 0);
      }
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [searchedTrips, stageFilter, sort, tripMatchesTeam]);

  const openTrip = (trip) => {
    navigate(getPartnerTripPath(trip));
  };

  const filteredPipelineTrips = useMemo(() => pipelineTrips.filter((trip) => matchesQuery([
    trip.title,
    trip.client_name,
    trip.client_email,
  ], search)), [pipelineTrips, search]);

  const tripsByStage = useMemo(() => {
    const grouped = Object.fromEntries(pipelineStages.map((stage) => [stage, []]));
    filteredPipelineTrips.forEach((trip) => {
      const stage = trip.pipeline_stage || 'planning';
      if (grouped[stage]) grouped[stage].push(trip);
      else grouped.planning?.push(trip);
    });
    return grouped;
  }, [filteredPipelineTrips, pipelineStages]);

  const openOpsTrip = (tripId) => {
    if (tripId) navigate(`/partner/trips/${tripId}`);
  };

  const renderOpsStatusChip = (status) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: STATUS_DOT[status] || DOCUMENT.ink3,
          flexShrink: 0,
        }}
      />
      <Typography component="span" sx={{ fontSize: 13 }}>
        {t(`partnerOps.opsStatus.${status}`, { defaultValue: status })}
      </Typography>
    </Box>
  );

  const filteredTravelingRows = useMemo(() => {
    if (travelingStatusFilter === 'all') return travelingRows;
    return travelingRows.filter((row) => row.status === travelingStatusFilter);
  }, [travelingRows, travelingStatusFilter]);

  const renderReadinessChip = (readiness) => (
    <Chip
      size="small"
      label={t(`partnerOps.readiness.${readiness}`, { defaultValue: readiness })}
      sx={{
        height: 22,
        borderRadius: 0,
        fontSize: 11,
        bgcolor: readiness === 'ready' ? 'rgba(46,125,50,0.12)' : 'rgba(140,43,34,0.1)',
        color: readiness === 'ready' ? '#2E7D32' : DOCUMENT.oxblood,
      }}
    />
  );

  const renderTravelingList = () => {
    if (!travelingRows.length) {
      return (
        <PartnerEmptyState
          title={t('partnerOps.noTraveling')}
          body={t('partnerOps.everythingLooksGood')}
        />
      );
    }
    const columns = [
      t('partnerOps.colTraveler'),
      t('partnerOps.colDestination'),
      t('partnerOps.colTripDay'),
      t('partnerOps.colNext'),
      t('partnerOps.colStatus'),
      t('partnerOps.colAttention'),
    ];
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={travelingStatusFilter}
              onChange={(event) => setTravelingStatusFilter(event.target.value)}
              displayEmpty
              data-testid="partner-ops-traveling-filter"
              sx={{ borderRadius: 0, bgcolor: DOCUMENT.surface, fontSize: 13 }}
            >
              <MenuItem value="all">{t('partnerOps.travelingFilter.all')}</MenuItem>
              {['on_track', 'needs_attention', 'urgent', 'awaiting_client', 'awaiting_supplier'].map((status) => (
                <MenuItem key={status} value={status}>
                  {t(`partnerOps.opsStatus.${status}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {!filteredTravelingRows.length ? (
          <PartnerEmptyState
            title={t('partnerOps.travelingFilterEmpty')}
            body={t('partnerOps.everythingLooksGood')}
          />
        ) : (
          <>
            <Box
              component="table"
              data-testid={OPS_LIST_TEST_IDS.traveling}
              sx={{ width: '100%', borderCollapse: 'collapse', mt: 1, display: { xs: 'none', md: 'table' } }}
            >
              <Box component="thead">
                <Box component="tr">
                  {columns.map((label, index) => (
                    <Box key={label} component="th" sx={{ ...opsThSx, pl: index === 0 ? 0 : 2 }}>{label}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {filteredTravelingRows.map((row) => (
                  <Box
                    component="tr"
                    key={row.tripId}
                    onClick={() => openOpsTrip(row.tripId)}
                    sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#F5F0E6' } }}
                  >
                    <Box component="td" sx={{ ...opsTdSx, pl: 0 }}>{row.clientName}</Box>
                    <Box component="td" sx={opsTdSx}>{row.destination || row.title}</Box>
                    <Box component="td" sx={opsTdSx}>
                      {row.tripDay && row.tripTotalDays
                        ? `${row.tripDay}/${row.tripTotalDays}`
                        : row.tripDayLabel || '—'}
                    </Box>
                    <Box component="td" sx={opsTdSx}>
                      {row.nextEvent
                        ? `${row.nextEvent.name}${row.nextEvent.startDatetime ? ` ${formatEventWhen(row.nextEvent.startDatetime)}` : ''}`
                        : '—'}
                    </Box>
                    <Box component="td" sx={opsTdSx}>{renderOpsStatusChip(row.status)}</Box>
                    <Box component="td" sx={opsTdSx}>{row.attention || '—'}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mt: 1 }} data-testid={OPS_LIST_TEST_IDS.traveling}>
              {filteredTravelingRows.map((row) => (
                <Box
                  key={row.tripId}
                  component="button"
                  type="button"
                  onClick={() => openOpsTrip(row.tripId)}
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    bgcolor: DOCUMENT.surface,
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                    p: 2,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{row.clientName}</Typography>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5 }}>
                    {row.destination || row.title}
                    {' · '}
                    {row.tripDay && row.tripTotalDays
                      ? `${row.tripDay}/${row.tripTotalDays}`
                      : row.tripDayLabel}
                  </Typography>
                  {row.nextEvent ? (
                    <Typography sx={{ fontSize: 13, mt: 0.75 }}>
                      {row.nextEvent.name}
                      {row.nextEvent.startDatetime ? ` ${formatEventWhen(row.nextEvent.startDatetime)}` : ''}
                    </Typography>
                  ) : null}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25, alignItems: 'center' }}>
                    {renderOpsStatusChip(row.status)}
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                      {row.attention || '—'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </>
    );
  };

  const renderDepartingList = () => {
    if (!departingRows.length) {
      return (
        <PartnerEmptyState
          title={t('partnerOps.noDeparting')}
          body={t('partnerOps.everythingLooksGood')}
        />
      );
    }
    return (
      <>
        <Box
          component="table"
          data-testid={OPS_LIST_TEST_IDS.departing}
          sx={{ width: '100%', borderCollapse: 'collapse', mt: 1, display: { xs: 'none', md: 'table' } }}
        >
          <Box component="thead">
            <Box component="tr">
              {[t('partnerOps.colClient'), t('partnerOps.colDestination'), t('partnerOps.colDates'), t('partnerOps.colReadiness'), t('partnerOps.colChecks'), t('partnerOps.advisor')].map((label, index) => (
                <Box key={label} component="th" sx={{ ...opsThSx, pl: index === 0 ? 0 : 2 }}>{label}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {departingRows.map((row) => (
              <Box
                component="tr"
                key={row.tripId}
                onClick={() => openOpsTrip(row.tripId)}
                sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#F5F0E6' } }}
              >
                <Box component="td" sx={{ ...opsTdSx, pl: 0 }}>{row.clientName}</Box>
                <Box component="td" sx={opsTdSx}>{row.destination || row.title}</Box>
                <Box component="td" sx={opsTdSx}>{formatOpsDateRange(row.startDate, row.endDate)}</Box>
                <Box component="td" sx={opsTdSx}>{renderReadinessChip(row.readiness)}</Box>
                <Box component="td" sx={opsTdSx}>
                  {(row.checks || []).map((check) => check.label).join(' · ') || '—'}
                </Box>
                <Box component="td" sx={opsTdSx}>{row.advisorName || t('partnerOps.unassigned')}</Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mt: 1 }} data-testid={OPS_LIST_TEST_IDS.departing}>
          {departingRows.map((row) => (
            <Box
              key={row.tripId}
              component="button"
              type="button"
              onClick={() => openOpsTrip(row.tripId)}
              sx={{
                width: '100%',
                textAlign: 'left',
                bgcolor: DOCUMENT.surface,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                p: 2,
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: 'inherit',
              }}
            >
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{row.clientName}</Typography>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5 }}>{row.destination || row.title}</Typography>
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                {formatOpsDateRange(row.startDate, row.endDate)}
              </Typography>
              <Box sx={{ mt: 1 }}>{renderReadinessChip(row.readiness)}</Box>
              {(row.checks || []).length ? (
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink2, mt: 1 }}>
                  {(row.checks || []).map((check) => check.label).join(' · ')}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </>
    );
  };

  const renderFlightsList = () => {
    if (!flightAlerts.length) {
      return (
        <PartnerEmptyState
          title={t('partnerOps.noFlights')}
          body={t('partnerOps.everythingLooksGood')}
        />
      );
    }
    return (
      <>
        <Box
          component="table"
          data-testid={OPS_LIST_TEST_IDS.flights}
          sx={{ width: '100%', borderCollapse: 'collapse', mt: 1, display: { xs: 'none', md: 'table' } }}
        >
          <Box component="thead">
            <Box component="tr">
              {[t('partnerOps.colFlight'), t('partnerOps.colStatus'), t('partnerOps.colDates'), t('partnerOps.colTrip')].map((label, index) => (
                <Box key={label} component="th" sx={{ ...opsThSx, pl: index === 0 ? 0 : 2 }}>{label}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {flightAlerts.map((alert) => (
              <Box
                component="tr"
                key={alert.id}
                onClick={() => openOpsTrip(alert.tripId)}
                sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#F5F0E6' } }}
              >
                <Box component="td" sx={{ ...opsTdSx, pl: 0 }}>{alert.flightNumber || '—'}</Box>
                <Box component="td" sx={opsTdSx}>{alert.status || '—'}</Box>
                <Box component="td" sx={opsTdSx}>
                  {alert.scheduledDeparture ? formatEventWhen(alert.scheduledDeparture) : '—'}
                </Box>
                <Box component="td" sx={opsTdSx}>
                  <Box component="span" sx={{ color: DOCUMENT.oxblood, textDecoration: 'underline' }}>
                    {t('partnerOps.view')}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mt: 1 }} data-testid={OPS_LIST_TEST_IDS.flights}>
          {flightAlerts.map((alert) => (
            <Box
              key={alert.id}
              component="button"
              type="button"
              onClick={() => openOpsTrip(alert.tripId)}
              sx={{
                width: '100%',
                textAlign: 'left',
                bgcolor: DOCUMENT.surface,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                p: 2,
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: 'inherit',
              }}
            >
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{alert.flightNumber}</Typography>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5 }}>{alert.status}</Typography>
              {alert.scheduledDeparture ? (
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                  {formatEventWhen(alert.scheduledDeparture)}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </>
    );
  };

  if ((!isOpsMode && loading) || (isOpsMode && opsLoading)) {
    return (
      <PartnerPageShell fullPage={viewMode === 'board' && !isOpsMode}>
        <PartnerLoading />
      </PartnerPageShell>
    );
  }

  return (
    <PartnerPageShell
      error={error}
      onClearError={() => setError('')}
      success={successNotice}
      onClearSuccess={() => setSuccessNotice('')}
      fullPage={viewMode === 'board' && !isOpsMode}
    >
      <PartnerPageHeader
        compact={viewMode === 'board' && !isOpsMode}
        title={isOpsMode ? t(`partnerOps.${opsMode}Title`) : t('partnerLists.tripsTitle')}
        subtitle={isOpsMode ? t(`partnerOps.${opsMode}Sub`) : t('partnerLists.tripsSub')}
        actions={
          isOpsMode ? (
            <Button sx={partnerOutlineBtnSx} onClick={() => navigate('/partner/trips')}>
              {t('partnerOps.backToTrips')}
            </Button>
          ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              sx={viewMode === 'list' ? partnerOxbloodBtnSx : partnerOutlineBtnSx}
              onClick={() => setViewMode('list')}
            >
              {t('partnerPipeline.listView')}
            </Button>
            <Button
              sx={viewMode === 'board' ? partnerOxbloodBtnSx : partnerOutlineBtnSx}
              onClick={() => setViewMode('board')}
            >
              {t('partnerPipeline.boardView')}
            </Button>
            {viewMode === 'list' && completedCount > 0 && (
              <Button
                data-testid="archive-all-completed-trips"
                sx={partnerOutlineBtnSx}
                onClick={() => setBulkArchiveDialogOpen(true)}
              >
                {t('partnerLists.archiveAllPast')}
              </Button>
            )}
            <PartnerDataExportButton dataset="trips" label={t('partnerExport.exportTrips')} testId="partner-trips-export" />
            <Button sx={partnerOxbloodBtnSx} onClick={() => navigate('/trips/create')}>
              {t('partnerLists.newTrip')}
            </Button>
          </Box>
          )
        }
      />

      {!isOpsMode && <EmailForwardingCard compact variant="partner" />}

      {isOpsMode ? (
        opsMode === 'traveling' ? renderTravelingList()
          : opsMode === 'departing' ? renderDepartingList()
            : renderFlightsList()
      ) : (
      <>
      {viewMode === 'list' ? (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mt: '19px',
        mb: 1,
      }}
      >
        <PartnerTeamMemberFilter
          value={teamMemberFilter}
          onChange={setTeamMemberFilter}
          isPartnerUser={partnerSession.isPartnerUser}
          getAuthHeaders={getPartnerAuthHeaders}
          testId="partner-trips-team-filter"
          sx={{ py: 0 }}
        />
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <PartnerFilterBar
            bordered={false}
            search={search}
            onSearchChange={handleListSearch}
            searchPlaceholder={t('partnerLists.searchTrips')}
            sort={sort}
            onSortChange={setSort}
            sortOptions={[
              { id: 'edited', label: t('partnerLists.sortEdited') },
              { id: 'name', label: t('partnerLists.sortName') },
              { id: 'client', label: t('partnerLists.sortClient') },
              { id: 'destination', label: t('partnerLists.sortDestination') },
              { id: 'departure', label: t('partnerLists.sortDeparture') },
              { id: 'return', label: t('partnerLists.sortReturn') },
            ]}
          />
        </Box>
      </Box>
      ) : (
      <PartnerTeamMemberFilter
        value={teamMemberFilter}
        onChange={setTeamMemberFilter}
        isPartnerUser={partnerSession.isPartnerUser}
        getAuthHeaders={getPartnerAuthHeaders}
        testId="partner-trips-team-filter"
      />
      )}
      {viewMode === 'list' ? (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          overflowX: 'auto',
          borderBottom: `1px solid ${DOCUMENT.rule}`,
        }}
      >
        {[{ id: 'all', label: t('partnerLists.filterAll') }, ...DEFAULT_PIPELINE_STAGES.map((stage) => ({
          id: stage,
          label: t(`partnerPipeline.stages.${stage}`),
        }))].map((stage) => {
          const selected = stageFilter === stage.id;
          return (
            <Box
              key={stage.id}
              component="button"
              type="button"
              onClick={() => setStageFilter(stage.id)}
              sx={{
                flex: '1 0 auto',
                border: 0,
                bgcolor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                px: 0.5,
                pt: 0.75,
                pb: 1,
                borderBottom: selected ? `2px solid ${DOCUMENT.oxblood}` : '2px solid transparent',
                color: selected ? DOCUMENT.oxblood : DOCUMENT.ink2,
              }}
            >
              <Typography sx={{
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily: DOCUMENT.sans,
                color: 'inherit',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
              >
                {stage.label}
              </Typography>
              <Typography sx={{ fontSize: 18, fontFamily: DOCUMENT.display, color: 'inherit', lineHeight: 1.2, mt: 0.35 }}>
                {stageCounts[stage.id] || 0}
              </Typography>
            </Box>
          );
        })}
      </Box>
      ) : null}
      {viewMode === 'list' ? null : (
        <Box sx={{
          mt: 1.5,
          mb: 0,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
        >
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('partnerLists.searchTrips')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: DOCUMENT.ink3 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                bgcolor: DOCUMENT.surface,
                '& fieldset': { borderColor: DOCUMENT.ruleHard },
              },
            }}
          />
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
            {t('partnerPipeline.dragHint')}
          </Typography>
        </Box>
      )}

      {viewMode === 'board' ? (
        pipelineLoading && !pipelineTrips.length ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <PartnerLoading />
          </Box>
        ) : (
          <PartnerTripPipelineBoard
            stages={pipelineStages}
            tripsByStage={tripsByStage}
            stageUpdatingId={stageUpdatingId}
            onOpenTrip={(trip) => {
              persistPartnerTripsView('board');
              if (trip.cardType === 'inquiry') {
                navigate(`/partner/inquiries/${trip.inquiryId}`);
                return;
              }
              navigate(`/partner/trips/${trip.tripId || trip.id}`);
            }}
            onStageChange={handlePipelineStageChange}
          />
        )
      ) : visibleTrips.length === 0 ? (
        <PartnerEmptyState
          title={search || stageFilter !== 'all' ? t('partnerLists.noResults') : t('partnerLists.noTrips')}
          body={search || stageFilter !== 'all' ? t('partnerLists.noResultsBody') : t('partnerLists.noTripsBody')}
          action={!search && stageFilter === 'all' ? (
            <Button sx={partnerOxbloodBtnSx} onClick={() => navigate('/trips/create')}>
              {t('partnerLists.createTrip')}
            </Button>
          ) : null}
        />
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 1.5 }} alignItems="stretch" sx={{ mt: '20px !important' }}>
          {visibleTrips.map((trip) => {
            const currentStage = trip.pipeline_stage || 'planning';
            const nextStage = DEFAULT_PIPELINE_STAGES[DEFAULT_PIPELINE_STAGES.indexOf(currentStage) + 1];
            return (
              <Grid item xs={12} md={6} key={trip.id} sx={{ display: 'flex' }}>
                <PartnerWorkspaceCard
                  testId="partner-trip-card"
                  layout="stage"
                  coverSrc={trip.image_url && trip.image_url.trim() ? trip.image_url : null}
                  fallbackSrc={getTripImageUrl(
                    getTripPrimaryLocation(trip) || trip.location,
                    trip.trip_type,
                    trip.id
                  )}
                  alt={trip.title}
                  onClick={() => openTrip(trip)}
                  menuItems={[
                    {
                      label: trip.is_master_trip
                        ? t('partnerLists.manageGroup')
                        : t('partnerLists.manageItinerary'),
                      onClick: () => openTrip(trip),
                    },
                    ...DEFAULT_PIPELINE_STAGES.filter((stage) => stage !== currentStage).map((stage) => ({
                      label: `${t('partnerPipeline.moveTo')} ${t(`partnerPipeline.stages.${stage}`)}`,
                      onClick: () => advanceListTripStage(trip, stage),
                    })),
                    ...(!trip.partner_archived ? [{
                      label: t('partnerManage.editDetails'),
                      onClick: () => {
                        setSelectedTrip(trip);
                        setEditDetailsOpen(true);
                      },
                    }] : []),
                    ...(trip.trip_code ? [{
                      label: t('partnerLists.copyTripCode'),
                      onClick: () => {
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(trip.trip_code);
                        }
                      },
                    }] : []),
                    ...(trip.partner_archived ? [{
                      label: t('partnerLists.restoreTrip'),
                      onClick: () => {
                        setSelectedTrip(trip);
                        setArchiveDialogOpen(true);
                      },
                    }] : []),
                    ...(!trip.partner_archived && canDeletePartnerTrip(trip) ? [{
                      label: t('partnerLists.deleteTrip'),
                      onClick: () => {
                        setSelectedTrip(trip);
                        setDeleteDialogOpen(true);
                      },
                      danger: true,
                    }] : []),
                    ...(!trip.partner_archived && canArchivePartnerTrip(trip) ? [{
                      label: t('partnerLists.archiveTrip'),
                      onClick: () => {
                        setSelectedTrip(trip);
                        setArchiveDialogOpen(true);
                      },
                    }] : []),
                  ]}
                >
                  <Typography variant="h6" component="h2" sx={{ ...partnerCardTitleSx, fontSize: 22, minHeight: 0, mb: 0.5, pr: 0.5, lineHeight: 1.15 }}>
                    {trip.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 0.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getTripPrimaryLocation(trip) || trip.location || t('partnerLists.noDestination')}
                  </Typography>
                  {(trip.start_date || trip.end_date) && (
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink, mb: 0.35 }}>
                      {trip.start_date && trip.end_date
                        ? `${format(new Date(trip.start_date), 'MMM d')} — ${format(new Date(trip.end_date), 'MMM d, yyyy')}`
                        : format(new Date(trip.start_date || trip.end_date), 'MMM d, yyyy')}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 1 }}>
                    {t('partnerLists.clientLabel')} {getTripClientName(trip) || 'N/A'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 'auto' }}>
                    <PersonOutlineIcon sx={{ fontSize: 16, color: DOCUMENT.oxblood }} />
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.oxblood, fontFamily: DOCUMENT.sans }}>
                      {t(`partnerPipeline.stages.${currentStage}`)}
                    </Typography>
                  </Box>
                  {nextStage ? (
                    <Button
                      size="small"
                      data-testid="advance-trip-stage"
                      onClick={(event) => {
                        event.stopPropagation();
                        advanceListTripStage(trip, nextStage);
                      }}
                      sx={{ ...partnerOutlineBtnSx, mt: 1.25, alignSelf: 'flex-start', py: 0.85, px: 1.5, minHeight: 32 }}
                    >
                      {t('partnerPipeline.advanceTo', { stage: t(`partnerPipeline.stages.${nextStage}`) })}
                    </Button>
                  ) : null}

                  {!!trip.is_master_trip && trip.child_trips?.length > 0 && (
                    <Accordion
                      disableGutters
                      elevation={0}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        mb: 0.25,
                        border: `1px solid ${DOCUMENT.ruleHard}`,
                        borderRadius: '0 !important',
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: DOCUMENT.oxblood, fontSize: 18 }} />}
                        sx={{
                          bgcolor: 'rgba(74,14,21,0.04)',
                          minHeight: 32,
                          px: 1,
                          '& .MuiAccordionSummary-content': { my: 0.25 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <GroupIcon sx={{ fontSize: 16, color: DOCUMENT.oxblood }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12.5, color: DOCUMENT.oxblood }}>
                            {t('partnerLists.travelers', { count: trip.child_trips.length })}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense disablePadding>
                          {trip.child_trips.map((child, idx) => (
                            <React.Fragment key={child.id}>
                              {idx > 0 && <Divider component="li" />}
                              <ListItem
                                sx={{
                                  py: 1.25,
                                  px: 2,
                                  '&:hover': { bgcolor: 'rgba(74,14,21,0.04)' },
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/partner/trips/${child.id}`);
                                }}
                              >
                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                  <Avatar sx={{
                                    width: 32, height: 32,
                                    bgcolor: DOCUMENT.oxblood,
                                    fontSize: 14, fontWeight: 600
                                  }}>
                                    {getTravelerInitials(child)}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                      {getTravelerDisplayName(child)}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                      <EventNoteIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                      <Typography variant="caption" component="span" color="text.secondary">
                                        {child.personal_item_count || 0} {t('partnerLists.personalItems')}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/partner/trips/${child.id}`);
                                    }}
                                    sx={{ color: DOCUMENT.oxblood }}
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            </React.Fragment>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </PartnerWorkspaceCard>
              </Grid>
            );
          })}
        </Grid>
      )}

      {viewMode === 'list' ? (
        <PartnerNote>{t('partnerLists.tripsNote')}</PartnerNote>
      ) : null}
      </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !actionLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>{t('partnerLists.deleteTripTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('partnerLists.deleteTripBody', { title: selectedTrip?.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
            {t('partnerLists.cancel')}
          </Button>
          <Button
            onClick={handleDeleteTrip}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : t('partnerLists.deleteTrip')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={archiveDialogOpen} onClose={() => !actionLoading && setArchiveDialogOpen(false)}>
        <DialogTitle>
          {selectedTrip?.partner_archived
            ? t('partnerLists.restoreTripTitle')
            : t('partnerLists.archiveTripTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedTrip?.partner_archived
              ? t('partnerLists.restoreTripBody', { title: selectedTrip?.title })
              : t('partnerLists.archiveTripBody', { title: selectedTrip?.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={actionLoading}>
            {t('partnerLists.cancel')}
          </Button>
          <Button
            onClick={() => handleArchiveTrip(!selectedTrip?.partner_archived)}
            variant="contained"
            disabled={actionLoading}
            sx={partnerOxbloodBtnSx}
          >
            {actionLoading ? (
              <CircularProgress size={20} />
            ) : selectedTrip?.partner_archived
              ? t('partnerLists.restoreTrip')
              : t('partnerLists.archiveTrip')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkArchiveDialogOpen}
        onClose={() => !actionLoading && setBulkArchiveDialogOpen(false)}
      >
        <DialogTitle>{t('partnerLists.archiveAllPastTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('partnerLists.archiveAllPastBody', { count: completedCount })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkArchiveDialogOpen(false)} disabled={actionLoading}>
            {t('partnerLists.cancel')}
          </Button>
          <Button
            data-testid="confirm-archive-all-completed-trips"
            onClick={handleBulkArchiveCompletedTrips}
            variant="contained"
            disabled={actionLoading || completedCount === 0}
            sx={partnerOxbloodBtnSx}
          >
            {actionLoading ? <CircularProgress size={20} /> : t('partnerLists.archiveAllPast')}
          </Button>
        </DialogActions>
      </Dialog>

      <PartnerTripDetailsEditor
        open={editDetailsOpen}
        onClose={() => {
          setEditDetailsOpen(false);
          setSelectedTrip(null);
        }}
        trip={selectedTrip}
        mode={selectedTrip?.is_master_trip ? 'group' : 'client'}
        onSaved={fetchTrips}
      />
    </PartnerPageShell>
  );
};

export default PartnerTrips;
